import { Text } from "@solomon-apps/shared/src/components/ui/text";
import type { BigDecimal } from "@solomon-labs/bigdecimal";
import React from "react";

import { formatObservedUsdv } from "@/hooks/positionAmounts";

import { HintTip } from "../../shared/HintTip";
import {
    LOADING,
    type StatReading,
    UNAVAILABLE
} from "../portfolioKeyStatsModel";

interface PortfolioKeyStatsProps {
    /** Total USDv held across the client's positions (live balances +
     *  manual amounts, from `usePortfolioTotals`). Null while loading. */
    totalUsdvPositions: BigDecimal | null;
    isLoadingPositions?: boolean;
    /** Protocol's advertised APY (`resolveCurrentApy`). */
    currentApy: StatReading;
    /** Total USDv yield earned to date (Σ position lifetime rewards). */
    totalUsdvEarned: BigDecimal | null;
    /** Rewards accrued but not yet paid (`resolvePendingRewards`). */
    totalUsdvPending: StatReading;
    /** Trailing 30-day realized APY (`resolveHistoricalApy`). */
    historicalApy: StatReading;
    /** Number of positions. */
    totalPositions: number;
    /** Forwarded to `KeyStatsRow` — set `false` under a bordered header. */
    topBorder?: boolean;
}

/**
 * The Portfolio key-stats row — a fine-print label over an H2 value, six
 * across, divided by hairlines (the dashboard pattern). Full-bleed inside
 * `ClientPage` (each child owns its own padding). Figures without a source
 * read "—" and carry their reason in the label's hint; the APYs carry their
 * definition there too. Derivations live in `portfolioKeyStatsModel`.
 */
export function PortfolioKeyStats({
    totalUsdvPositions,
    isLoadingPositions = true,
    currentApy,
    totalUsdvEarned,
    totalUsdvPending,
    historicalApy,
    totalPositions,
    topBorder
}: PortfolioKeyStatsProps): React.ReactElement {
    // Amounts stay decimal through formatting, including large LP principal.
    const usd = (n: BigDecimal): string => formatObservedUsdv(n);
    return (
        <KeyStatsRow
            topBorder={topBorder}
            stats={[
                {
                    label: "Total USDv in Positions",
                    value: totalUsdvPositions
                        ? usd(totalUsdvPositions)
                        : isLoadingPositions
                          ? LOADING
                          : UNAVAILABLE
                },
                { label: "Current APY", ...currentApy },
                {
                    label: "Total USDv Earned",
                    value:
                        totalUsdvEarned === null
                            ? isLoadingPositions
                                ? LOADING
                                : UNAVAILABLE
                            : usd(totalUsdvEarned)
                },
                { label: "Total USDv Pending", ...totalUsdvPending },
                { label: "Historical APY", ...historicalApy },
                {
                    label: "Total Positions",
                    value: totalPositions.toLocaleString("en-US")
                }
            ]}
        />
    );
}

/* ── Mechanism ───────────────────────────────────────────── */

export interface KeyStat {
    label: string;
    value: string;
    /** On-demand explanation (definition, or why the value is "—"), shown
     *  as a hint beside the label. */
    hint?: string;
}

/** Column-count ladder keyed by number of stats, driven by CONTAINER width
 *  (`@container/keystats`) so the row wraps by its own width, not the viewport.
 *  Stacked (1-col) below `@xl`; an intermediate wrap (e.g. 5 → 3-across, "2×3")
 *  before going full-width. Static classes so Tailwind keeps them in the build. */
const GRID_COLS: Record<number, string> = {
    2: "@xl/keystats:grid-cols-2",
    3: "@xl/keystats:grid-cols-3",
    4: "@xl/keystats:grid-cols-2 @2xl/keystats:grid-cols-4",
    // Drop to the 3-across ("2×3") wrap by @5xl so the wide $-values aren't
    // squeezed at the awkward in-between widths.
    5: "@xl/keystats:grid-cols-3 @5xl/keystats:grid-cols-5",
    // 2×3, then 3×2, then one row only once each cell has room for a
    // seven-figure $-value at H2 (@7xl ≈ 213px a cell).
    6: "@xl/keystats:grid-cols-2 @3xl/keystats:grid-cols-3 @7xl/keystats:grid-cols-6"
};

/**
 * Generic key-stats row — `n` fine-print-label-over-H2-value cells, hairline
 * divided, top+bottom bordered, full-bleed (each cell owns its padding).
 * `PortfolioKeyStats` is one named composition over this; the position
 * detail is another.
 */
export function KeyStatsRow({
    stats,
    topBorder = true
}: {
    stats: KeyStat[];
    /** Draw the top rule. Default `true` (standalone banded row). Set `false`
     *  when the row sits directly under an already-bordered page header
     *  (e.g. `ClientPage`) so the two rules don't stack into a 2px line. */
    topBorder?: boolean;
}): React.ReactElement {
    const cols = GRID_COLS[stats.length] ?? "@xl/keystats:grid-cols-4";
    return (
        <section
            className={`@container/keystats ${
                topBorder ? "border-y" : "border-b"
            } border-line-base`}
        >
            {/* Stacked (<@xl): a single column of compact left/right rows with NO
                per-row divider — only the section's top/bottom rule frames it.
                @xl+: the n-across grid. Hairlines come from a 1px gap over a
                line-colored background (cells sit on the ground surface) rather
                than `divide-x`, so when the row WRAPS to multiple lines the
                dividers render correctly in both directions with no stray edges. */}
            <div
                className={`grid grid-cols-1 ${cols} py-2 @xl/keystats:py-0 @xl/keystats:gap-px @xl/keystats:bg-line-base`}
            >
                {stats.map((s) => (
                    <StatCell
                        key={s.label}
                        label={s.label}
                        value={s.value}
                        hint={s.hint}
                    />
                ))}
                {/* 5 stats in the 3-col wrap (@xl–@4xl) leave one empty trailing
                    slot; without a cell there the grid's line-colored background
                    shows through. Fill it with a ground cell (shown only in that
                    range) so the blank reads as plain surface. */}
                {stats.length === 5 && (
                    <div
                        aria-hidden
                        className="bg-surface-ground hidden @xl/keystats:block @5xl/keystats:hidden"
                    />
                )}
            </div>
        </section>
    );
}

function StatCell({
    label,
    value,
    hint
}: {
    label: string;
    value: string;
    hint?: string;
}): React.ReactElement {
    const hintTip = hint ? (
        <HintTip text={hint} label={`About ${label}`} />
    ) : null;
    return (
        <div className="px-4 md:px-6 py-1 @xl/keystats:py-5 @xl/keystats:bg-surface-ground">
            {/* Stacked (<@xl) — label ↔ value on one line, same type variant
                (color is the only differentiator), space-efficient. */}
            <div className="flex items-baseline justify-between gap-4 @xl/keystats:hidden">
                <span className="flex min-w-0 items-center gap-1">
                    <Text
                        variant="body"
                        render={<span />}
                        className="text-foreground-muted truncate"
                    >
                        {label}
                    </Text>
                    {hintTip}
                </span>
                <Text
                    variant="body"
                    render={<span />}
                    className="text-foreground-base shrink-0"
                >
                    {value}
                </Text>
            </div>
            {/* @xl+ — fine-print label over an H2 value (the dashboard cell). */}
            <div className="hidden @xl/keystats:block">
                <span className="flex items-center gap-1">
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        {label}
                    </Text>
                    {hintTip}
                </span>
                <Text
                    variant="h2"
                    render={<span />}
                    className="text-foreground-base block mt-1"
                >
                    {value}
                </Text>
            </div>
        </div>
    );
}
